import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Search, ShieldCheck, ArrowRight } from "lucide-react";

const ACTION_COLORS = {
  create: "bg-emerald-100 text-emerald-700",
  update: "bg-[#D4AF37]/15 text-[#0A192F]",
  delete: "bg-red-100 text-red-700",
  login: "bg-slate-100 text-slate-600",
  allocate: "bg-[#0F4C81]/15 text-[#0F4C81]",
  publish: "bg-emerald-100 text-emerald-700",
  issue: "bg-[#D4AF37]/15 text-[#0A192F]",
};

export default function AuditLogsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [action, setAction] = useState("");
  const LIMIT = 20;

  const load = async () => {
    const q = new URLSearchParams({
      page: String(page),
      limit: String(LIMIT),
      sort: "-created_at",
    });
    if (search) q.set("search", search);
    if (action) q.set("action", action);
    const { data } = await api.get(`/v2/audit-logs?${q.toString()}`);
    setItems(data.items || []);
    setTotal(data.total || 0);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, search, action]);

  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Compliance
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Audit trail
            </h1>
            <p className="mt-2 text-slate-500 text-sm">
              {total.toLocaleString()} logged events. Every mutation. Every actor. Forever.
            </p>
          </div>
          <ShieldCheck className="h-10 w-10 text-[#D4AF37]" />
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              placeholder="Search action or resource…"
              className="pl-10 h-11"
              data-testid="audit-search-input"
            />
          </div>
          <Select value={action} onValueChange={(v) => { setPage(1); setAction(v === "all" ? "" : v); }}>
            <SelectTrigger className="w-[200px] h-11" data-testid="audit-action-filter"><SelectValue placeholder="Any action" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              <SelectItem value="create">create</SelectItem>
              <SelectItem value="update">update</SelectItem>
              <SelectItem value="delete">delete</SelectItem>
              <SelectItem value="allocate">allocate</SelectItem>
              <SelectItem value="publish">publish</SelectItem>
              <SelectItem value="issue">issue</SelectItem>
              <SelectItem value="invite">invite</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mt-6 rounded-xl bg-white border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.18em] text-slate-500">
              <tr>
                <th className="text-left px-5 py-3">When</th>
                <th className="text-left px-5 py-3">Actor</th>
                <th className="text-left px-5 py-3">Action</th>
                <th className="text-left px-5 py-3">Resource</th>
                <th className="text-left px-5 py-3">Details</th>
              </tr>
            </thead>
            <tbody>
              {items.map((log) => (
                <tr key={log.id} className="border-t border-slate-100 hover:bg-slate-50/80" data-testid={`audit-row-${log.id}`}>
                  <td className="px-5 py-3 font-mono text-[10px] text-slate-500">
                    {log.created_at ? new Date(log.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-5 py-3">
                    <p className="text-sm font-semibold text-[#0A192F]">{log.actor_email || "system"}</p>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{log.actor_role || "—"}</p>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${ACTION_COLORS[log.action] || "bg-slate-100 text-slate-600"}`}>
                      {log.action || "—"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-600 font-mono text-xs">
                    {log.resource || "—"}
                    {log.resource_id && (
                      <span className="ml-1 text-slate-400">({log.resource_id})</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500 max-w-md truncate">
                    {log.details ? JSON.stringify(log.details) : "—"}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="text-center py-12 text-sm text-slate-500">
                  No audit entries match.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-xs text-slate-500">Page {page} of {pages}</p>
          <div className="flex gap-2">
            <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="audit-prev-button">Prev</Button>
            <Button variant="outline" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))} data-testid="audit-next-button">
              Next <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
